import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { MapPin, Users, Calendar, Sword, Sparkles, Store } from 'lucide-react';

const Landing = () => {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [easterEggClicks, setEasterEggClicks] = useState(0);

    const features = [
        {
            icon: <MapPin className="w-8 h-8" />,
            title: 'LOCATE_GAMES',
            description: 'Find tabletop sessions within your radius using our real-time map interface'
        },
        {
            icon: <Users className="w-8 h-8" />,
            title: 'JOIN_SESSIONS',
            description: 'Connect with local players for MTG, D&D, and board game adventures'
        },
        {
            icon: <Calendar className="w-8 h-8" />,
            title: 'HOST_EVENTS',
            description: 'Create and manage your own game sessions with ease'
        },
        {
            icon: <Sword className="w-8 h-8" />,
            title: 'DND_TOOLS',
            description: 'Upload character sheets and find Dungeon Masters in your area'
        },
        {
            icon: <Sparkles className="w-8 h-8" />,
            title: 'MTG_SEARCH',
            description: 'Search commanders and look up card details with Scryfall integration'
        },
        {
            icon: <Store className="w-8 h-8" />,
            title: 'LGS_NETWORK',
            description: 'Discover Local Game Stores and their hosted events'
        }
    ];

    return (
        <div className="min-h-screen bg-[#050505] overflow-hidden">
            {/* Scanlines overlay */}
            <div className="scanlines" />
            
            {/* Matrix background effect */}
            <div className="matrix-bg" />

            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center justify-center hero-bg">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#050505]/50 to-[#050505]" />
                
                <div className="relative z-10 container mx-auto px-6 py-20 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        {/* Terminal header */}
                        <div className="inline-block mb-8">
                            <div className="terminal-header rounded-t-none">
                                <div className="terminal-dot red" />
                                <div className="terminal-dot yellow" />
                                <div className="terminal-dot green" />
                                <span className="ml-4 text-[#00ff41] text-sm font-mono">tabletop_looper.exe</span>
                            </div>
                        </div>

                        <h1 className="text-5xl sm:text-6xl lg:text-8xl font-bold text-[#00ff41] glow-text mb-6 tracking-widest">
                            TABLETOP<br/>LOOPER
                        </h1>
                        
                        <p className="text-lg sm:text-xl text-[#008f11] max-w-2xl mx-auto mb-4 font-mono">
                            {'>'} INITIALIZING CONNECTION TO LOCAL GAMING NETWORK...
                        </p>
                        <p className="text-base text-[#00ff41]/70 max-w-xl mx-auto mb-12 font-mono cursor-blink">
                            Find and join tabletop gaming sessions in real-time
                        </p>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                        >
                            <Button
                                data-testid="login-button"
                                onClick={login}
                                className="bg-[#00ff41] text-black hover:bg-[#00ff41]/80 px-12 py-6 text-xl font-bold tracking-wider rounded-none border-2 border-[#00ff41] transition-all duration-300 hover:shadow-[0_0_30px_rgba(0,255,65,0.5)]"
                            >
                                {'>'} CONNECT_NOW
                            </Button>
                        </motion.div>
                    </motion.div>

                    {/* Scroll indicator */}
                    <motion.div
                        className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
                        animate={{ y: [0, 10, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                    >
                        <div className="w-6 h-10 border-2 border-[#00ff41] rounded-full flex justify-center">
                            <div className="w-1 h-3 bg-[#00ff41] rounded-full mt-2" />
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Features Section */}
            <section className="relative py-20 bg-[#050505] grid-lines">
                <div className="container mx-auto px-6">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl sm:text-5xl font-bold text-[#00ff41] glow-text mb-4 tracking-wider">
                            SYSTEM_FEATURES
                        </h2>
                        <p className="text-[#008f11] font-mono">
                            {'>'} Modules loaded successfully
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((feature, index) => (
                            <motion.div
                                key={feature.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="terminal-window p-6 card-hover"
                            >
                                <div className="text-[#00ff41] mb-4">
                                    {feature.icon}
                                </div>
                                <h3 className="text-xl font-bold text-[#00ff41] mb-2 tracking-wider">
                                    {feature.title}
                                </h3>
                                <p className="text-[#008f11] text-sm font-mono">
                                    {feature.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Game Types Section */}
            <section className="relative py-20 bg-[#0a0a0a]">
                <div className="container mx-auto px-6">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl sm:text-5xl font-bold text-[#00ff41] glow-text mb-4 tracking-wider">
                            SUPPORTED_PROTOCOLS
                        </h2>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {/* MTG */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="terminal-window overflow-hidden"
                        >
                            <div className="terminal-header">
                                <span className="text-[#00ff41] text-sm">magic_the_gathering.dll</span>
                            </div>
                            <div className="p-6">
                                <div className="w-full h-48 bg-gradient-to-br from-[#1a1a2e] to-[#16213e] flex items-center justify-center mb-4 border border-[#003b00]">
                                    <span className="text-6xl">🎴</span>
                                </div>
                                <h3 className="text-2xl font-bold text-[#00ff41] mb-2">MTG</h3>
                                <p className="text-[#008f11] text-sm font-mono">
                                    Commander search, card lookup, draft sessions
                                </p>
                            </div>
                        </motion.div>

                        {/* D&D */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            className="terminal-window overflow-hidden"
                        >
                            <div className="terminal-header">
                                <span className="text-[#00ff41] text-sm">dungeons_and_dragons.dll</span>
                            </div>
                            <div className="p-6">
                                <div className="w-full h-48 bg-gradient-to-br from-[#2d132c] to-[#801336] flex items-center justify-center mb-4 border border-[#003b00]">
                                    <span className="text-6xl">🐉</span>
                                </div>
                                <h3 className="text-2xl font-bold text-[#00ff41] mb-2">D&D</h3>
                                <p className="text-[#008f11] text-sm font-mono">
                                    Character sheets, DM finder, campaign tracker
                                </p>
                            </div>
                        </motion.div>

                        {/* Board Games */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 }}
                            className="terminal-window overflow-hidden"
                        >
                            <div className="terminal-header">
                                <span className="text-[#00ff41] text-sm">board_games.dll</span>
                            </div>
                            <div className="p-6">
                                <div className="w-full h-48 bg-gradient-to-br from-[#0c3c26] to-[#1a5c3a] flex items-center justify-center mb-4 border border-[#003b00] relative">
                                    {/* Die emoji with clickable red dot overlay */}
                                    <span className="text-6xl">🎲</span>
                                    {/* Invisible clickable area over the red dot of the die */}
                                    <motion.div
                                        onClick={() => {
                                            const newClicks = easterEggClicks + 1;
                                            setEasterEggClicks(newClicks);
                                            if (newClicks >= 5) {
                                                navigate('/4th-dimension');
                                            }
                                        }}
                                        whileHover={{ scale: 1.2 }}
                                        whileTap={{ scale: 0.9 }}
                                        className="absolute w-6 h-6 cursor-pointer rounded-full"
                                        style={{ 
                                            top: '35%', 
                                            left: '50%', 
                                            transform: 'translateX(-50%)',
                                        }}
                                        title=""
                                    />
                                </div>
                                <h3 className="text-2xl font-bold text-[#00ff41] mb-2">BOARD GAMES</h3>
                                <p className="text-[#008f11] text-sm font-mono">
                                    All your favorite tabletop experiences
                                </p>
                                {/* Subtle hint after 3 clicks */}
                                {easterEggClicks >= 3 && easterEggClicks < 5 && (
                                    <motion.p 
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="text-[#330011] text-xs font-mono mt-2"
                                    >
                                        {5 - easterEggClicks} more...
                                    </motion.p>
                                )}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative py-20 bg-[#050505]">
                <div className="container mx-auto px-6 text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        className="terminal-window p-12 max-w-2xl mx-auto glow-box"
                    >
                        <h2 className="text-3xl sm:text-4xl font-bold text-[#00ff41] mb-6 tracking-wider">
                            READY_TO_CONNECT?
                        </h2>
                        <p className="text-[#008f11] mb-8 font-mono">
                            {'>'} Join the network of tabletop enthusiasts in your area
                        </p>
                        <Button
                            data-testid="cta-login-button"
                            onClick={login}
                            className="bg-transparent border-2 border-[#00ff41] text-[#00ff41] hover:bg-[#00ff41] hover:text-black px-10 py-4 text-lg font-bold tracking-wider rounded-none transition-all duration-300"
                        >
                            {'>'} INITIALIZE_CONNECTION
                        </Button>
                    </motion.div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 border-t border-[#003b00] bg-[#050505]">
                <div className="container mx-auto px-6 text-center">
                    <p className="text-[#008f11] text-sm font-mono">
                        {'>'} TABLETOP_LOOPER v1.0.0 | SYSTEM_STATUS: ONLINE
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default Landing;
